import { Navigate, Route, Routes, useLocation } from 'react-router'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import {
  AssociatedPartiesPage,
  AssociatedPartyDetailsPage,
} from '@/features/associated-parties'
import {
  PackagingItemDetailsPage,
  PackedItemDetailsPage,
  PackedItemsPage,
  PackagingItemsPage,
  PackagingItemIssuesPage,
  PackagingSupplierDetailsPage,
  PackagingSuppliersPage,
  ShipmentDetailPage,
  ShipmentsPage,
} from '@/features/packaging-suppliers'
import { machineryDirectiveComplianceRoute } from '@/features/compliance-studio/machinery-directive/routes'
import { LegislationCitationPage } from '@/features/llm-chat/LegislationCitationPage'
import { LlmChatPage } from '@/features/llm-chat/LlmChatPage'
import {
  ProductCatalogListPage,
  ProductDetailPage,
} from '@/features/product-catalog'
import { NewsroomPage } from '@/features/regulatory-news'
import { Settings } from '@/features/settings'
import { StandardsLibrary } from '@/features/standards-library'
import { StandardDetail } from '@/features/standards-library/pages/StandardDetail'
import { ArtemEarthOnlyRoute } from './ArtemEarthOnlyRoute'
import { getSettingsChildRoutes } from './settingsChildRoutes'
import { V2AuthenticatedLayout } from '@/v2/V2AuthenticatedLayout'
import V2ComponentsPage from '@/v2/pages/V2ComponentsPage'
import V2ComponentDetailPage from '@/v2/pages/V2ComponentDetailPage'
import V2ProductsPage from '@/v2/pages/V2ProductsPage'
import V2ProductDetailPage from '@/v2/pages/V2ProductDetailPage'
import V2SupplierDetailPage from '@/v2/pages/V2SupplierDetailPage'
import V2SuppliersPage from '@/v2/pages/V2SuppliersPage'
import { V2_PRODUCTS_PATH } from '@/v2/utils/routes'
import {
  LegacyPackagingItemDetailRedirect,
  LegacyPackagingSupplierDetailRedirect,
  LegacyPackagingSuppliersListRedirect,
} from './LegacyPackagingSuppliersRedirect'
import { PackagingSuppliersRoute } from './PackagingSuppliersRoute'

function LegacyProjectPathRedirect() {
  const location = useLocation()
  return (
    <Navigate
      to={`/product-catalog${location.search}${location.hash}`}
      replace
    />
  )
}

export default function App() {
  return (
    <Routes>
      <Route path='/' element={<AuthenticatedLayout />}>
        {/* Keep product catalog as the default landing page for authenticated users. */}
        <Route index element={<Navigate to='/product-catalog' replace />} />
        <Route path='llm-chat' element={<LlmChatPage />} />
        <Route path='llm-chat/:sessionId' element={<LlmChatPage />} />
        <Route
          path='lex/:celex/:startBlockId/:endBlockId'
          element={<LegislationCitationPage />}
        />
        <Route
          path='newsroom'
          element={
            <ArtemEarthOnlyRoute>
              <NewsroomPage />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='newsroom/inbox'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='newsroom/settings'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news/inbox'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route
          path='regulatory-news/settings'
          element={
            <ArtemEarthOnlyRoute>
              <Navigate to='/newsroom' replace />
            </ArtemEarthOnlyRoute>
          }
        />
        <Route path='product-catalog' element={<ProductCatalogListPage />} />
        <Route
          path='product-catalog/:productId/*'
          element={<ProductDetailPage />}
        />
        <Route path='standards-library' element={<StandardsLibrary />} />
        <Route path='associated-parties' element={<AssociatedPartiesPage />} />
        <Route
          path='associated-parties/:associatedPartyId'
          element={<AssociatedPartyDetailsPage />}
        />
        <Route
          path='packaging/suppliers'
          element={
            <PackagingSuppliersRoute>
              <PackagingSuppliersPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/suppliers/:packagingSupplierId'
          element={
            <PackagingSuppliersRoute>
              <PackagingSupplierDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/suppliers/:packagingSupplierId/items/:itemId'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packaging-items'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/issues'
          element={
            <PackagingSuppliersRoute>
              <PackagingItemIssuesPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packed-items'
          element={
            <PackagingSuppliersRoute>
              <PackedItemsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/shipments'
          element={
            <PackagingSuppliersRoute>
              <ShipmentsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/shipments/:shipmentId'
          element={
            <PackagingSuppliersRoute>
              <ShipmentDetailPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging/packed-items/:packedItemId'
          element={
            <PackagingSuppliersRoute>
              <PackedItemDetailsPage />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingSuppliersListRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers/:packagingSupplierId'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingSupplierDetailRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='packaging-suppliers/:packagingSupplierId/items/:itemId'
          element={
            <PackagingSuppliersRoute>
              <LegacyPackagingItemDetailRedirect />
            </PackagingSuppliersRoute>
          }
        />
        <Route
          path='standards-library/standards/:standardId'
          element={<StandardDetail />}
        />
        {machineryDirectiveComplianceRoute}
        <Route path='project/*' element={<LegacyProjectPathRedirect />} />
        <Route path='settings' element={<Settings />}>
          {getSettingsChildRoutes()}
        </Route>
      </Route>
      <Route path='v2' element={<V2AuthenticatedLayout />}>
        <Route index element={<Navigate to={V2_PRODUCTS_PATH} replace />} />
        <Route path='products' element={<V2ProductsPage />} />
        <Route path='products/:productId/*' element={<V2ProductDetailPage />} />
        <Route path='components' element={<V2ComponentsPage />} />
        <Route path='components/:componentId' element={<V2ComponentDetailPage />} />
        <Route path='suppliers' element={<V2SuppliersPage />} />
        <Route path='suppliers/:supplierId' element={<V2SupplierDetailPage />} />
        <Route path='orgs-roles' element={<AssociatedPartiesPage />} />
        <Route
          path='orgs-roles/:associatedPartyId'
          element={<AssociatedPartyDetailsPage />}
        />
        <Route path='settings' element={<Settings />}>
          <Route index element={<Navigate to='account' replace />} />
          {getSettingsChildRoutes()}
        </Route>
      </Route>
      <Route path='*' element={<Navigate to='/' replace />} />
    </Routes>
  )
}
